import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { colors, chartColors } from "../../Shared/colors";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LayersIcon from "@mui/icons-material/Layers";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {
  useGetSingleHierarchyQuery,
  useGetActiveStaffQuery,
  useGetDashboardCompleteQuery,
} from "../../features/employeeSlice";
import { useGetAllNotificationsQuery } from "../../features/notificationSlice";
import { Autocomplete, TextField, Skeleton } from "@mui/material";

import NotificationModel from "../notificationModel/NotificationModel";
import { MeetingTasksModal } from "./MeetingTasksModal";
import { decryptData } from "../../services/crypto";

export const DashboardFirst = ({ staff, setStaff, showModal }) => {
  const [data, setData] = useState({});
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  let role = null;
  let storedStaffRole = localStorage.getItem("role");

  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      console.error("Failed to decrypt data:", error);
    }
  }

  const { data: notificationsData, isLoading, error } = useGetAllNotificationsQuery();
  const [notificationMessage, setNotificationMessages] = useState('');
  const [showModalLocal, setShowModalLocal] = useState(showModal);


  useEffect(() => {
    if (notificationsData && Array.isArray(notificationsData) && notificationsData.length > 0) {
      const hasShownModal = localStorage.getItem("notificationShown");
      if (!hasShownModal || hasShownModal === "false") {
        setNotificationMessages([...notificationsData]);
        setShowModalLocal(true);
        localStorage.setItem("notificationShown", "true");
      }
    }
  }, [notificationsData]);


  let staffId = null;
  let storedStaffId = localStorage.getItem("id");

  if (storedStaffId) {
    try {
      staffId = decryptData(storedStaffId);
    } catch (error) {
      console.error("Failed to decrypt data:", error);
    }
  }
  if (!staffId) {
    console.error("Staff ID not found in localStorage");
  }

  const {
    data: hierarchyStaff,
    error: hierarchyError,
  } = useGetSingleHierarchyQuery({ staffId });
  const { data: allStaff } = useGetActiveStaffQuery();

  const staffOptions = useMemo(() => {
    let options = [];
    // Filter to exclude employees with role = "ts" (telesales agents)
    hierarchyStaff
      ?.filter((staff) => staff?.role?.toLowerCase() !== "ts")
      ?.map((staff) =>
      options.push({
        id: staff?._id,
        label: staff?.firstName + " " + staff?.lastName,
      })
    );

    return options;
  }, [hierarchyStaff]);

  const allStaffOptions = useMemo(() => {
    let options = [];
    // Filter to exclude employees with role = "ts" (telesales agents)
    allStaff
      ?.filter((staff) => staff?.role?.toLowerCase() !== "ts")
      ?.map((staff) =>
      options.push({
        id: staff?._id,
        label: staff?.firstName + " " + staff?.lastName,
      })
    );

    return options;
  }, [allStaff]);

  const selectedStaff =
    staffOptions.find((option) => option.id === staff?.id) || null;

  // Use optimized dashboard API
  const { data: dashboardData, isFetching } = useGetDashboardCompleteQuery({ staffId: staff?.id || staffId })

  useEffect(() => {
    if (dashboardData?.profileData) {
      setData({
        meetingCount: dashboardData.countMeeting?.totalMeetings || 0,
        dpCounts: dashboardData.profileData?.dpCounts || 0,
        pdpCounts: dashboardData.profileData?.pdpCounts || 0
      })
    }
  }, [dashboardData])

  const handleCloseModal = () => {
    setShowModalLocal(false);
  };

  if (hierarchyError) {
    return <div>Error loading data</div>;
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading notifications</div>;
  }

  return (
    <Main>
      <DashboardHeader>
        <StaffSelectionCard>
          <StaffInputField>
            <h3>Select Staff Member</h3>
            <Autocomplete
              id="staff-autocomplete"
              size="small"
              options={
                role === "ceo" || role === "admin" || role === "account"
                  ? allStaffOptions
                  : staffOptions
              }
              getOptionLabel={(option) => option.label}
              style={{
                width: "100%",
                borderRadius: "8px",
                minHeight: "3em",
                marginTop: "0.5em",
                overflow: "hidden",
              }}
              onChange={(e, value) => {
                setStaff(value || null);
              }}
              clearOnEscape
              value={selectedStaff}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search staff member..."
                  sx={{
                    backgroundColor: `${colors.white}`,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      border: `1px solid ${colors.inputBorderWhite}`,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: chartColors.primary,
                        borderWidth: '2px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: chartColors.primary,
                        borderWidth: '2px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    },
                  }}
                />
              )}
            />
          </StaffInputField>
        </StaffSelectionCard>
        
        <MetricsGrid>
          <MetricCard 
            onClick={() => setShowMeetingModal(true)} 
            style={{ cursor: 'pointer' }}
            title="Click to view meeting details"
          >
            <CardIcon>
              <CalendarMonthIcon />
            </CardIcon>
            <CardContent>
              <h3>Meetings</h3>
              <h2>{isFetching ? <Skeleton width={40} height={32} /> : data?.meetingCount ?? "0"}</h2>
            </CardContent>
          </MetricCard>
          
          <MetricCard>
            <CardIcon>
              <AttachMoneyIcon />
            </CardIcon>
            <CardContent>
              <h3>Down Payments</h3>
              <h2>{isFetching ? <Skeleton width={40} height={32} /> : data?.dpCounts ?? "0"}</h2>
            </CardContent>
          </MetricCard>
          
          <MetricCard>
            <CardIcon>
              <LayersIcon />
            </CardIcon>
            <CardContent>
              <h3>Partial Payments</h3>
              <h2>{isFetching ? <Skeleton width={40} height={32} /> : data?.pdpCounts ?? "0"}</h2>
            </CardContent>
          </MetricCard>
        </MetricsGrid>
      </DashboardHeader>
      
      {showModalLocal && (
        <>
          <Overlay onClick={handleCloseModal} />
          <NotificationModel
            notifications={notificationMessage}
            onClose={handleCloseModal}
          />
        </>
      )}
      
      <MeetingTasksModal
        open={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        staff={staff}
      />
    </Main>
  );
};
const Main = styled.div`
  padding: 2rem;
  background: transparent;
`;

const DashboardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 3rem;
  }
`;

const StaffSelectionCard = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 300px;
  position: relative;
  overflow: hidden;
  
  @media (min-width: 1024px) {
    max-width: 400px;
  }
`;

const StaffInputField = styled.div`
  h3 {
    color: ${colors.text};
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  flex: 2;
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const MetricCard = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 20px 20px 0 0;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:nth-child(1)::after {
    background: linear-gradient(90deg, #667eea, #764ba2);
  }
  
  &:nth-child(2)::after {
    background: linear-gradient(90deg, #f093fb, #f5576c);
  }
  
  &:nth-child(3)::after {
    background: linear-gradient(90deg, #4facfe, #00f2fe);
  }
`;

const CardIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 16px;
  flex-shrink: 0;
  box-shadow: 
    0 8px 16px rgba(102, 126, 234, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
  }

  .MuiSvgIcon-root {
    color: ${colors.white};
    font-size: 1.75rem;
    z-index: 1;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
`;

const CardContent = styled.div`
  flex: 1;
  
  h3 {
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0 0 0.75rem 0;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;
  }

  h2 {
    font-size: 2.5rem;
    font-weight: 800;
    background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    line-height: 1;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); 
  backdrop-filter: blur(5px); 
  z-index: 999;
`;
